import _ from 'lodash';
import React from 'react';

import {
  Add,
  DeleteForever
} from '@mui/icons-material';
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  fields,
  Form,
  formUtil
} from 'iron-forge-react-modules';
import {
  BaseWidget,
  selectFields
} from 'modules/layer01';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import { appFormUtil } from 'modules/layer00';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const { genController } = selectFields.NetSuitePaymentMethodSelectField.genComponentAndControllerGenerator();
const ControlledPaymentMethodSelectField = genController<types.IFormState>();
const ControlledCheckBox = fields.CheckboxField.genControllerComponent<types.IFormState>();



export interface IProps {
  storeId: string;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.storeId);
  const [savePaymentMethods, savePaymentMethodsTuple] = dataService.useSavePaymentMethods(props.storeId);

  const form = useForm<types.IFormState>({
    defaultValues: {
      paymentMethods: []
    }
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'paymentMethods'
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['paymentMethods']);



  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      const trimmedFormState = appFormUtil.trimFormStateValues(formState);
      const uniqueRows = _.uniqBy(trimmedFormState.paymentMethods, s => s.paymentMethodText);
      if (uniqueRows.length !== trimmedFormState.paymentMethods.length) {
        alert("The mapped strings must be unique for this store.");
        return;
      }
      savePaymentMethods(trimmedFormState);
    },
    [savePaymentMethods]
  );

  const handleClickNew = React.useCallback(
    () => {
      fieldArray.append({
        id: '',
        isTaxable: true,
        paymentMethodText: '',
        netSuitePaymentMethod: null
      });
    },
    [fieldArray]
  );

  const handleClickDelete = React.useCallback(
    (index: number) => {
      fieldArray.remove(index);
    },
    [fieldArray]
  );

  const handleClickAddUnmappedString = React.useCallback(
    (paymentMethodText: string) => {
      fieldArray.append({
        id: '',
        isTaxable: true,
        paymentMethodText,
        netSuitePaymentMethod: null
      });
    },
    [fieldArray]
  );



  const loading = queryTuple.loading || savePaymentMethodsTuple.loading;


  const unmappedStrings = React.useMemo(
    () => _.filter(queryTuple.data.unmappedStrings, str => {
      const found = _.find(fieldArray.fields, field => field.paymentMethodText === str);
      return _.isNil(found);
    }),
    [queryTuple.data.unmappedStrings, fieldArray.fields]
  );

  return (
    <Form.FC
      disableDefaultOverflow
      onSubmit={form.handleSubmit(processSubmit)}>
      <BaseWidget.FC
        title="Payment Methods"
        elAction={(
          <Button
            type="submit"
            variant="contained">
            Save
          </Button>
        )}>
        <Grid container>
          <Grid
            container
            item
            xs={12}
            md={8}
            rowSpacing={2}
            columnSpacing={1}>
            {fieldArray.fields.map((field, index) => (
              <React.Fragment key={field.id}>
                <Grid
                  item
                  xs={5}
                  md={4}>
                  <ControlledTextField
                    isDisabled={loading}
                    fieldKey={`paymentMethods.${index}.paymentMethodText`}
                    formControl={form.control} />
                </Grid>

                <Grid
                  item
                  xs={6}
                  md={5}>
                  <ControlledPaymentMethodSelectField
                    isDisabled={loading}
                    isRequired
                    fieldKey={`paymentMethods.${index}.netSuitePaymentMethod`}
                    formControl={form.control} />
                </Grid>

                <Grid
                  item
                  xs={2}>
                  <ControlledCheckBox
                    isDisabled={loading}
                    fieldKey={`paymentMethods.${index}.isTaxable`}
                    label="Taxable"
                    formControl={form.control} />
                </Grid>

                <Grid
                  item
                  xs={2}
                  md={1}>
                  <IconButton
                    disabled={loading}
                    onClick={() => handleClickDelete(index)}>
                    <DeleteForever />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}

            <Grid
              item
              xs={12}>
              <Button
                disabled={loading}
                startIcon={(<Add />)}
                onClick={handleClickNew}>
                New Payment Method
              </Button>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}>
            <Typography>Suggested mappings for this store:</Typography>

            <List>
              {_.map(unmappedStrings, str => (
                <ListItem key={str}>
                  <ListItemButton
                    onClick={() => handleClickAddUnmappedString(str)}>
                    <ListItemIcon color="warning">
                      <Add />
                    </ListItemIcon>

                    <ListItemText
                      primary={str}
                      color="warning" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </BaseWidget.FC>
    </Form.FC>
  );
};
